import type { Document } from "../document/Document";
import type { Layer, SmartObjectLayer } from "../document/Layer";
import { createSmartObjectLayer } from "../document/Layer";
import type { Command } from "./Command";

/**
 * Convert a layer to a Smart Object. The source layer is wrapped inside an
 * embedded Document, and a SmartObjectLayer replaces it in the tree.
 *
 * Photoshop equivalent: Layer → Smart Objects → Convert to Smart Object.
 *
 * Invert: extracts the original layer back out of the Smart Object, restoring
 * the tree to its pre-conversion state.
 */
export class ConvertToSmartObjectCommand implements Command {
  readonly kind = "convert-to-smart-object";

  constructor(
    private readonly layerId: string,
    private readonly smartObjectId?: string,
  ) {}

  apply(doc: Document): Document {
    const newLayers = convertLayerInTree(doc.layers, this.layerId, doc.width, doc.height, this.smartObjectId);
    if (newLayers === doc.layers) return doc; // not found
    return { ...doc, layers: newLayers };
  }

  invert(before: Document, _after: Document): Command {
    return new RasterizeSmartObjectCommand(this.smartObjectId ?? findSmartObjectId(before.layers, this.layerId));
  }
}

/**
 * Rasterize a Smart Object — extract its embedded content back as a regular
 * layer. This is the inverse of ConvertToSmartObjectCommand.
 *
 * Photoshop equivalent: Layer → Smart Objects → Rasterize.
 */
export class RasterizeSmartObjectCommand implements Command {
  readonly kind = "rasterize-smart-object";

  constructor(
    private readonly smartObjectId: string,
  ) {}

  apply(doc: Document): Document {
    const newLayers = rasterizeInTree(doc.layers, this.smartObjectId);
    if (newLayers === doc.layers) return doc; // not found
    return { ...doc, layers: newLayers };
  }

  invert(before: Document, _after: Document): Command {
    // Find the original layer that was inside the Smart Object
    const so = findSmartObject(before.layers, this.smartObjectId);
    if (!so) return new RasterizeSmartObjectCommand(this.smartObjectId);
    // The first layer in the embedded doc is what we wrapped
    const originalLayer = so.embedded.layers[0];
    if (!originalLayer) return new RasterizeSmartObjectCommand(this.smartObjectId);
    return new ConvertToSmartObjectCommand(originalLayer.id, this.smartObjectId);
  }
}

function convertLayerInTree(
  layers: readonly Layer[],
  targetId: string,
  docWidth: number,
  docHeight: number,
  smartObjectId?: string,
): readonly Layer[] {
  for (let i = 0; i < layers.length; i++) {
    const l = layers[i]!;
    if (l.id === targetId) {
      // Don't convert groups or smart objects themselves
      if (l.kind === "group" || l.kind === "smart-object") return layers;
      const so = createSmartObjectLayer(l, docWidth, docHeight, smartObjectId);
      const newLayers = [...layers];
      newLayers[i] = so;
      return newLayers;
    }
    if (l.kind === "group") {
      const newChildren = convertLayerInTree(l.children, targetId, docWidth, docHeight, smartObjectId);
      if (newChildren !== l.children) {
        const newLayers = [...layers];
        newLayers[i] = { ...l, children: newChildren };
        return newLayers;
      }
    }
  }
  return layers;
}

function rasterizeInTree(
  layers: readonly Layer[],
  smartObjectId: string,
): readonly Layer[] {
  for (let i = 0; i < layers.length; i++) {
    const l = layers[i]!;
    if (l.kind === "smart-object" && l.id === smartObjectId) {
      // Extract the first layer from the embedded document
      const embedded = l.embedded;
      if (embedded.layers.length === 0) return layers;
      const extracted = embedded.layers[0]!;
      // Position the extracted layer at the Smart Object's position
      const positioned = { ...extracted, x: l.x, y: l.y };
      const newLayers = [...layers];
      newLayers[i] = positioned;
      return newLayers;
    }
    if (l.kind === "group") {
      const newChildren = rasterizeInTree(l.children, smartObjectId);
      if (newChildren !== l.children) {
        const newLayers = [...layers];
        newLayers[i] = { ...l, children: newChildren };
        return newLayers;
      }
    }
  }
  return layers;
}

function findSmartObject(layers: readonly Layer[], id: string): SmartObjectLayer | null {
  for (const l of layers) {
    if (l.kind === "smart-object" && l.id === id) return l;
    if (l.kind === "group") {
      const inner = findSmartObject(l.children, id);
      if (inner) return inner;
    }
  }
  return null;
}

function findSmartObjectId(layers: readonly Layer[], originalLayerId: string): string {
  for (const l of layers) {
    if (l.kind === "smart-object") {
      // Check if the embedded doc contains the original layer
      if (l.embedded.layers.some(el => el.id === originalLayerId)) {
        return l.id;
      }
    }
    if (l.kind === "group") {
      const inner = findSmartObjectId(l.children, originalLayerId);
      if (inner) return inner;
    }
  }
  return originalLayerId; // fallback
}
